import { useId, useState } from 'react'
import { Info, Plus, Send, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { AddNamesDialog } from '@/components/custom/deal-review/deal-lead-naming'
import { SendBatchConfirmDialog } from '@/components/custom/deal-review/send-batch-confirm-dialog'
import { MUTED_TEXT } from '@/features/deal-review/review-tones'
import { useDealLeadCounts } from '@/hooks/use-deal-leads'
import {
  useFetchSendBatchPreview,
  type UndoableSend,
} from '@/hooks/use-deal-send-batches'
import { isUndoableSend } from '@/features/deal-review/send-batch'
import { displayNameFor } from '@/features/deal-review/deal-lead-views'
import type { DealLeadListItem } from '@/types/api'

export interface LeadSelection {
  ids: ReadonlySet<string>
  toggle: (id: string) => void
  clear: () => void
  selectAll: (ids: string[]) => void
}

const BAR_GROUP = 'flex shrink-0 items-center gap-2'

function BarDivider() {
  return (
    <Separator
      orientation="vertical"
      className="h-px w-full shrink-0 bg-primary/10 xl:h-5 xl:w-px"
    />
  )
}

// TooltipContent is aria-hidden: the sr-only description already carries the text.
function BarNotes({ label, notes }: { label: string; notes: string[] }) {
  const notesId = useId()
  if (notes.length === 0) return null

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            aria-describedby={notesId}
            className={`inline-flex size-7 shrink-0 items-center justify-center rounded-full ${MUTED_TEXT} hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            <Info aria-hidden="true" className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent aria-hidden="true" className="flex flex-col gap-1.5">
          {notes.map((note) => (
            <span key={note}>{note}</span>
          ))}
        </TooltipContent>
      </Tooltip>
      <span id={notesId} className="sr-only">
        {notes.join(' ')}
      </span>
    </>
  )
}

const PAGED_SELECTION_NOTE =
  'The list is paged, so this covers the rows loaded so far, not every deal matching these filters.'

// Absolute, not fixed: PageContainer's will-change-transform makes it the containing block.
export function SelectionBar({
  selection,
  leads,
  send,
}: {
  selection: LeadSelection
  leads: DealLeadListItem[]
  /** Owned by the page, so the undo window outlives the bar. */
  send: UndoableSend
}) {
  const count = selection.ids.size
  if (count === 0) return null

  const allSelected = leads.length > 0 && count >= leads.length

  return (
    <div
      // The positioner spans the table, so it must not swallow clicks on rows beside the bar.
      className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-4"
    >
      <div
        role="region"
        aria-label="Bulk actions for the selected deals"
        className="pointer-events-auto flex max-h-[60vh] w-fit max-w-full animate-in flex-col items-stretch gap-2 overflow-y-auto rounded-3xl border border-primary/15 bg-background p-2 shadow-lg duration-200 fade-in slide-in-from-bottom-2 xl:flex-row xl:flex-wrap xl:items-center xl:gap-x-3 xl:gap-y-2 xl:pr-3"
      >
        <div className={BAR_GROUP}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Clear selection"
            className="size-7 shrink-0 rounded-full text-primary/60 hover:text-primary"
            onClick={selection.clear}
          >
            <X aria-hidden="true" className="size-4" />
          </Button>

          <Typography
            as="span"
            variant="body-sm"
            className="shrink-0 whitespace-nowrap font-medium text-primary"
          >
            {allSelected ? `All ${count} loaded selected` : `${count} selected`}
          </Typography>

          {!allSelected && leads.length > count && (
            <Button
              variant="link"
              size="sm"
              className="h-auto shrink-0 p-0 text-secondary"
              onClick={() => selection.selectAll(leads.map((lead) => lead.id))}
            >
              Select all {leads.length}
            </Button>
          )}

          {allSelected && (
            <BarNotes
              label="About this selection"
              notes={[PAGED_SELECTION_NOTE]}
            />
          )}
        </div>

        <BarDivider />

        <BulkSendControl selection={selection} leads={leads} send={send} />
      </div>
    </div>
  )
}

// Every row-level reason is stated by the confirm dialog's preview, not here.
function BulkSendControl({
  selection,
  leads,
  send,
}: {
  selection: LeadSelection
  leads: DealLeadListItem[]
  send: UndoableSend
}) {
  const counts = useDealLeadCounts()
  const [naming, setNaming] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [checking, setChecking] = useState(false)
  const busy = checking || send.pending
  const fetchPreview = useFetchSendBatchPreview()
  // Only selected rows still loaded are sent; the selection is not pruned on refetch.
  const picked = leads.filter((lead) => selection.ids.has(lead.id))
  const count = picked.length
  const writeEnabled = counts.data?.dealCloudWriteEnabled
  const canSend = counts.data?.canSend
  const ready = writeEnabled === true && canSend === true && count > 0
  const unnamed = ready
    ? picked.filter(
        (lead) => displayNameFor(lead) === null && lead.capabilities.canCorrect
      )
    : []

  const notes: string[] = []
  if (writeEnabled === false) {
    notes.push(
      picked.find((lead) => lead.capabilities.sendBlockedReason)?.capabilities
        .sendBlockedReason ?? 'Sending to DealCloud is off here.'
    )
  } else if (canSend === false) {
    notes.push('You can view sends but not start them.')
  } else if (unnamed.length > 0) {
    notes.push(
      `${unnamed.length} of the ${count} ${
        unnamed.length === 1 ? 'has' : 'have'
      } no name, which DealCloud requires. Add ${
        unnamed.length === 1 ? 'it' : 'them'
      } first.`
    )
  }

  const startSend = async () => {
    if (busy) return
    setChecking(true)
    const sent = picked.map(({ id, version }) => ({ id, version }))
    let preview
    try {
      preview = await fetchPreview(sent)
    } catch {
      // The dialog shows the preview error with Try again.
      preview = undefined
    } finally {
      setChecking(false)
    }
    if (preview && isUndoableSend(preview)) {
      send.start(sent, preview, selection.clear)
    } else {
      setConfirming(true)
    }
  }

  return (
    <>
      <div className={BAR_GROUP}>
        {unnamed.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => setNaming(true)}
          >
            <Plus aria-hidden="true" />
            {`Add ${unnamed.length} ${unnamed.length === 1 ? 'name' : 'names'}`}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          className="shrink-0"
          disabled={!ready || busy}
          aria-busy={busy}
          onClick={() => void startSend()}
        >
          <Send aria-hidden="true" />
          {`Send ${count} to DealCloud`}
        </Button>

        {/* Beside the button: a disabled button takes no hover or focus. */}
        <BarNotes label="About this send" notes={notes} />
      </div>

      <AddNamesDialog
        leads={unnamed}
        open={naming && unnamed.length > 0}
        onOpenChange={setNaming}
      />

      <SendBatchConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        leads={picked.map(({ id, version }) => ({ id, version }))}
        onQueued={selection.clear}
      />
    </>
  )
}
